/*
 * CAPTURE-OWNED MODULE
 * Line Items section of Template Studio's field rail.
 *
 * A template has one line_items_table zone per page, and an AI-built template
 * can have dozens (HD Supply: 39). One row per zone pushed the Fields list
 * down to a sliver, so the section is a single summary row by default; the
 * per-zone list opens under a disclosure and scrolls inside a capped box.
 *
 * Zones on pages past the sample document's last page cannot be seen or
 * adjusted here; "Remove table zones beyond page N" deletes them after a
 * confirmation naming the count and what longer invoices lose: past the last
 * table zone, extraction reads each page from a default area (zones[0]'s
 * x/width, y 0.08-0.93; tableZoneExtractor's continuation pass).
 */
import React, { useState } from 'react';

/** "pages 1-53", "page 4" — page_index is zero-based, the text one-based. */
export function tableZonePagesText(tableZones) {
  if (tableZones.length === 0) return '';
  const pages = tableZones.map(z => (z.page_index ?? 0) + 1);
  const lo = Math.min(...pages);
  const hi = Math.max(...pages);
  return lo === hi ? `page ${lo}` : `pages ${lo}-${hi}`;
}

/** Table zones on a page past the sample's last page (one-based count). */
export function tableZonesBeyond(tableZones, samplePageCount) {
  if (!samplePageCount) return [];
  return tableZones.filter(z => (z.page_index ?? 0) + 1 > samplePageCount);
}

/**
 * @param {object}   props
 * @param {object[]} props.tableZones       line_items_table zones of the template
 * @param {number}   [props.samplePageCount] page count of the sample document, when known
 * @param {function} props.onSelectZone     (zone) => void
 * @param {function} props.onDeleteZone     (zoneId) => void
 * @param {function} props.onRemoveZones    (zoneIds) => Promise — deletes, one zone per call
 * @param {function} props.onDrawTableZone  () => void
 */
export default function LineItemsSection({
  tableZones,
  samplePageCount = null,
  onSelectZone,
  onDeleteZone,
  onRemoveZones,
  onDrawTableZone,
}) {
  const [expanded, setExpanded] = useState(false);
  const [removing, setRemoving] = useState(false);

  const sorted = [...tableZones].sort((p, q) => (p.page_index ?? 0) - (q.page_index ?? 0));
  const count = sorted.length;
  const beyond = tableZonesBeyond(sorted, samplePageCount);

  const handleRemoveBeyond = async () => {
    const n = beyond.length;
    const ok = window.confirm(
      `Remove ${n} table zone${n === 1 ? '' : 's'} on pages beyond page ${samplePageCount}?\n\n` +
      `The sample document has ${samplePageCount} page${samplePageCount === 1 ? '' : 's'}, so these zones cannot be seen or adjusted here.\n\n` +
      `Invoices longer than ${samplePageCount} page${samplePageCount === 1 ? '' : 's'} lose those pages' table zones. ` +
      `Line items on page ${samplePageCount + 1} and later are then read from a default area: ` +
      "a remaining table zone's width, most of the page height."
    );
    if (!ok) return;
    setRemoving(true);
    try {
      await onRemoveZones(beyond.map(z => z.id));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="line-items-toggle-section" data-testid="line-items-section">
      <div className="line-items-header">
        {count > 0 ? (
          <button
            type="button"
            className="line-items-summary"
            aria-expanded={expanded}
            aria-controls="line-items-zone-list"
            onClick={() => setExpanded(v => !v)}
            title={`Line Items: ${count} table zone${count === 1 ? '' : 's'} (${tableZonePagesText(sorted)}) — ${expanded ? 'hide' : 'show'} the list`}
          >
            <span className="line-items-disclosure" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
            <span className="line-items-summary-text">
              Line Items: {count} table zone{count === 1 ? '' : 's'} ({tableZonePagesText(sorted)})
            </span>
          </button>
        ) : (
          <div className="line-items-toggle-label">Line Items</div>
        )}
        <button
          type="button"
          className="btn btn-xs line-items-draw-btn"
          onClick={onDrawTableZone}
          title={count > 0
            ? "Draw a table zone on the page shown (replaces that page's table zone)"
            : 'Draw a zone around the table area on the PDF'}
        >
          {count > 0 ? '+ Draw' : '+ Draw Table Zone'}
        </button>
      </div>

      {expanded && count > 0 && (
        <div className="table-zone-list" id="line-items-zone-list" data-testid="table-zone-list">
          {sorted.map(tableZone => (
            <div className="table-zone-info" key={tableZone.id} data-testid="table-zone-row">
              <span className="table-zone-info-label">Page {(tableZone.page_index ?? 0) + 1}</span>
              <span className="table-zone-info-meta">
                {Number(tableZone.w * 100).toFixed(0)}% × {Number(tableZone.h * 100).toFixed(0)}%
              </span>
              <button
                type="button"
                className="btn-icon-sm"
                onClick={() => onSelectZone(tableZone)}
                title="Select table zone on document"
              >
                ✏️
              </button>
              <button
                type="button"
                className="btn-icon-sm btn-danger-icon"
                onClick={() => onDeleteZone(tableZone.id)}
                title={`Delete the page ${(tableZone.page_index ?? 0) + 1} table zone`}
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      )}

      {beyond.length > 0 && (
        <div className="line-items-actions">
          <button
            type="button"
            className="btn btn-xs line-items-prune-btn"
            onClick={handleRemoveBeyond}
            disabled={removing}
          >
            {removing ? 'Removing…' : `Remove table zones beyond page ${samplePageCount} (${beyond.length})`}
          </button>
        </div>
      )}
    </div>
  );
}
